import React, {useEffect, useMemo, useState} from "react";
import {Avatar} from "primereact/avatar";
import {Button} from "primereact/button";
import Icon from "../../../system/components/icon";
import api from "../../../system/api";
import {HELP_ARTICLES, HELP_CATEGORIES, HELP_POPULAR_TAGS, HELP_SETTINGS, HELP_TAG_MAP, HELP_VIDEOS} from "../help-content";
import {HelpSearch, searchItems} from "./help-sections";
import {PostContent} from "../post";

// Persian-only copy of the approved help-center design (content comes from help-content.js).
const T = {
    eyebrowHome: "مرکز آموزش و پشتیبانی زوفینو",
    help: "چطور می‌توانیم کمکتان کنیم؟",
    helpSub: "پاسخ کوتاه، راهنمای مرحله‌به‌مرحله یا ویدئوی مرتبط را پیدا کنید.",
    frequent: "جست‌وجوهای پرتکرار:",
    categories: "دسته‌بندی‌ها",
    categoriesSub: "از موضوعی شروع کنید که اکنون با آن کار دارید.",
    newbies: "برای کاربران تازه‌وارد",
    quickStart: "شروع سریع زوفینو",
    quickStartSub: "چهار گام کوتاه تا تیم شما آماده کار شود.",
    startPath: "شروع مسیر ۲۰ دقیقه‌ای",
    popular: "مقالات پربازدید",
    popularSub: "پاسخ‌هایی که بیشتر کاربران از آن‌ها شروع کرده‌اند.",
    allArticles: "همه مقالات",
    latestVideos: "تازه‌ترین ویدئوها",
    latestVideosSub: "آموزش‌های کوتاه و مستقیم؛ جدا از فهرست مقالات.",
    videoLibrary: "کتابخانه ویدئو",
    noAnswer: "پاسختان را پیدا نکردید؟",
    noAnswerSub: "موضوع را برای پشتیبانی بفرستید؛ جست‌وجوی شما هم برای بهبود هلپ‌سنتر ثبت می‌شود.",
    sendRequest: "ارسال درخواست پشتیبانی",
    article: "مقاله",
    guide: "راهنمای کوتاه",
    video: "ویدئو",
    results: "نتیجه",
    clear: "پاک‌کردن فیلترها",
    all: "همه",
    levels: ["مقدماتی", "میانی", "پیشرفته"],
    emptyTitle: "نتیجه‌ای پیدا نشد",
    emptySub: "محتوایی برای این عبارت نداریم. عبارت کوتاه‌تری امتحان کنید یا درخواستتان را برای پشتیبانی بفرستید.",
};

const typeLabel = (type) => (type === "video" ? T.video : type === "guide" ? T.guide : T.article);
const digits = (value) => String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
export const categoryOf = (id) => HELP_CATEGORIES.find((c) => c.id === id) || HELP_CATEGORIES[0];
const tagsOf = (item) => (item.tags?.length ? item.tags : HELP_TAG_MAP[item.cat] || []);
// Fire-and-forget analytics for the admin panel; never blocks the page.
const ping = (path, payload) => api.post(path, payload).catch(() => undefined);

// Unified, searchable list of static content + published CMS posts.
export function buildSearchItems(posts = []) {
    const staticItems = [...HELP_ARTICLES, ...HELP_VIDEOS].map((item) => ({
        key: item.id,
        kind: item.type === "video" ? "video" : "article",
        id: item.id,
        title: item.title,
        summary: item.summary,
        keywords: item.keywords,
        category: categoryOf(item.cat).title,
        tags: tagsOf(item),
        label: typeLabel(item.type),
        icon: item.type === "video" ? "play_circle" : "article",
        type: item.type,
        popular: item.popular,
        raw: item,
    }));
    const postItems = (posts || []).map((post) => ({
        key: `post:${post.documentId}`,
        kind: "post",
        documentId: post.documentId,
        title: post.title,
        category: post.post_category?.title,
        tags: Array.isArray(post.tags) ? post.tags : [],
        label: T.article,
        icon: "article",
        type: "article",
        popular: 0,
    }));
    return [...staticItems, ...postItems];
}

function Meta({item}) {
    return (
        <span className="flex flex-wrap align-items-center gap-2 text-2xs text-400">
            <span className="surface-100 border-round px-2 py-1">{typeLabel(item.type)}</span>
            <span>{categoryOf(item.cat).title}</span>
            <span>{item.type === "video" ? item.duration : item.time}</span>
            <span>{item.level}</span>
            {HELP_SETTINGS.updatedDate && item.updated && <span>به‌روزرسانی: {item.updated}</span>}
        </span>
    );
}

export function ArticleCard({item, onOpen}) {
    return (
        <div className="w-6 p-2 cursor-pointer" onClick={() => onOpen(item)}>
            <div className="border-1 border-300 border-round-xl bg-white p-3 text-xs flex flex-column gap-2 h-full">
                <Meta item={item}/>
                <span className="font-bold text-sm">{item.title}</span>
                <span className="text-400">{item.summary}</span>
            </div>
        </div>
    );
}

export function VideoCard({item, onOpen, width = "w-4"}) {
    return (
        <div className={`${width} p-2 cursor-pointer`} onClick={() => onOpen(item)}>
            <div className="border-1 border-300 border-round-xl bg-white text-xs flex flex-column h-full overflow-hidden">
                <div className="surface-200 flex align-items-center justify-content-center h-8rem relative">
                    <Icon icon="mi mi:play_circle" className="text-5xl text-primary"/>
                    <span className="absolute bottom-0 left-0 m-2 bg-black-alpha-60 text-white border-round px-2 py-1 text-2xs">{item.duration}</span>
                </div>
                <div className="p-3 flex flex-column gap-2">
                    <Meta item={item}/>
                    <span className="font-bold text-sm">{item.title}</span>
                    <span className="text-400">{item.summary}</span>
                </div>
            </div>
        </div>
    );
}

const Cards = ({items, onOpen}) => (
    <div className="flex flex-wrap">
        {items.map((item) => (item.type === "video"
            ? <VideoCard key={item.id} item={item} onOpen={onOpen}/>
            : <ArticleCard key={item.id} item={item} onOpen={onOpen}/>))}
    </div>
);

const Section = ({title, subtitle, action, onAction, children, tone = "", eyebrow}) => (
    <div className={`w-full p-6 flex align-items-center justify-content-center border-bottom-2 border-200 ${tone}`}>
        <div className="w-8 flex flex-column gap-3">
            <div className="flex align-items-center justify-content-between">
                <div className="flex flex-column gap-1">
                    {eyebrow && <span className="text-xs text-primary font-bold">{eyebrow}</span>}
                    <span className="font-bold text-lg">{title}</span>
                    {subtitle && <span className="text-xs text-400">{subtitle}</span>}
                </div>
                {action && <Button className="text-xs p-2 p-button-text" label={`${action} ←`} onClick={onAction}/>}
            </div>
            {children}
        </div>
    </div>
);

export const SupportStrip = ({onRequest}) => (
    <div className="w-full p-6 flex align-items-center justify-content-center border-bottom-2 border-200">
        <div className="w-8 flex align-items-center justify-content-between surface-100 border-2 border-200 border-dashed border-round-xl p-3">
            <div className="flex flex-column">
                <span className="font-bold text-xl">{T.noAnswer}</span>
                <span className="text-400 text-xs">{T.noAnswerSub}</span>
            </div>
            <Button className="text-xs" label={T.sendRequest} onClick={onRequest}/>
        </div>
    </div>
);

export function CategoryCards({go}) {
    return (
        <div className="flex flex-wrap">
            {HELP_CATEGORIES.map((category) => {
                const count = [...HELP_ARTICLES, ...HELP_VIDEOS].filter((i) => i.cat === category.id).length;
                return (
                    <div key={category.id} className="w-4 p-2 cursor-pointer" onClick={() => go({view: "category", cat: category.id})}>
                        <div className="border-1 border-300 border-round-xl bg-white p-3 text-xs flex flex-column gap-2 h-full">
                            <Avatar size="large" icon={<Icon icon={`mi mi:${category.icon}`}/>}/>
                            <span className="font-bold text-sm">{category.title}</span>
                            <span className="text-400">{category.desc}</span>
                            <span className="text-primary">{digits(count)} محتوا</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const getPathItems = () => [HELP_ARTICLES[0], HELP_VIDEOS[5], HELP_ARTICLES[2], HELP_VIDEOS[2]].filter(Boolean);
const PATH_STEPS = [
    ["ساخت فضای کاری", "حساب و سازمانتان را آماده کنید."],
    ["دعوت تیم", "افراد و نقش‌های مناسب را اضافه کنید."],
    ["تعریف هدف", "تغییر مورد انتظار را شفاف کنید."],
    ["شروع پروژه", "اولین خروجی قابل تحویل را بسازید."],
];

export function QuickStartSection({go}) {
    return (
        <Section eyebrow={T.newbies} title={T.quickStart} subtitle={T.quickStartSub} action={T.startPath} onAction={() => go({view: "path"})}>
            <div className="flex flex-wrap">
                {PATH_STEPS.map(([title, description], index) => (
                    <div key={title} className="w-3 p-2 cursor-pointer" onClick={() => go({view: "path"})}>
                        <div className="border-1 border-300 border-round-xl bg-white p-3 text-xs flex flex-column gap-2 h-full">
                            <Avatar shape="circle" label={digits(index + 1)}/>
                            <span className="font-bold text-sm">{title}</span>
                            <span className="text-400">{description}</span>
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
}

export function HomeView({go, openItem, onRequest, searchItemsList}) {
    const top = useMemo(() => [...HELP_ARTICLES].sort((a, b) => b.popular - a.popular).slice(0, 6), []);
    return (
        <>
            <div className="w-full flex align-items-center justify-content-center hero-banner surface-100 border-bottom-2 border-200 py-8">
                <div className="w-7 flex flex-column align-items-center justify-content-center gap-4">
                    <span className="text-xs text-primary font-bold">{T.eyebrowHome}</span>
                    <span className="text-5xl text-center font-bold">{T.help}</span>
                    <span className="text-400">{T.helpSub}</span>
                    <HelpSearch items={searchItemsList} onOpen={openItem} onSearch={(q) => go({view: "search", q})}/>
                    <div className="flex flex-wrap align-items-center justify-content-center gap-2 text-xs text-500">
                        <span>{T.frequent}</span>
                        {HELP_POPULAR_TAGS.slice(0, 4).map((tag) => (
                            <Button key={tag} className="p-button-text p-button-secondary text-xs p-1 px-2 border-1 border-300" label={tag} onClick={() => go({view: "search", q: tag})}/>
                        ))}
                    </div>
                </div>
            </div>
            <Section title={T.categories} subtitle={T.categoriesSub}><CategoryCards go={go}/></Section>
            {HELP_SETTINGS.quickStart && <QuickStartSection go={go}/>}
            {HELP_SETTINGS.popularArticles && <Section tone="surface-100" title={T.popular} subtitle={T.popularSub} action={T.allArticles} onAction={() => go({view: "articles"})}>
                <Cards items={top} onOpen={openItem}/>
            </Section>}
            {HELP_SETTINGS.newVideos && <Section title={T.latestVideos} subtitle={T.latestVideosSub} action={T.videoLibrary} onAction={() => go({view: "videos"})}>
                <div className="flex flex-wrap">{HELP_VIDEOS.slice(0, 3).map((video) => <VideoCard key={video.id} item={video} onOpen={openItem}/>)}</div>
            </Section>}
        </>
    );
}

const Chip = ({active, label, onClick}) => (
    <Button className="text-xs p-2 px-3" outlined={!active} label={label} onClick={onClick}/>
);

const Empty = ({onRequest}) => (
    <div className="flex flex-column align-items-center gap-2 p-6 text-center">
        <Icon icon="mi mi:search_off" className="text-5xl text-300"/>
        <span className="font-bold">{T.emptyTitle}</span>
        <span className="text-xs text-400 w-6">{T.emptySub}</span>
        <Button className="text-xs" label={T.sendRequest} onClick={onRequest}/>
    </div>
);

const PageTitle = ({eyebrow, title, subtitle}) => (
    <div className="w-full p-6 pb-3 flex justify-content-center surface-100 border-bottom-2 border-200">
        <div className="w-8 flex flex-column gap-1">
            <span className="text-xs text-primary font-bold">{eyebrow}</span>
            <span className="text-3xl font-bold">{title}</span>
            <span className="text-400 text-sm">{subtitle}</span>
        </div>
    </div>
);

/** Articles / videos listing with search, topic, level and tag filters. */
export function ListingView({kind, params, go, openItem, onRequest, searchItemsList}) {
    const isVideo = kind === "videos";
    const {cat = "all", level = "all", tag = "all", q = ""} = params;
    const base = isVideo ? HELP_VIDEOS : HELP_ARTICLES;
    let filtered = base.filter((item) => (cat === "all" || item.cat === cat)
        && (level === "all" || item.level === level)
        && (tag === "all" || tagsOf(item).includes(tag)));
    if (q) {
        const ids = new Set(searchItems(searchItemsList.filter((i) => i.raw), q, 100).map((i) => i.id));
        filtered = filtered.filter((item) => ids.has(item.id));
    }
    const set = (patch) => go({view: kind, cat, level, tag, q, ...patch});
    const dirty = cat !== "all" || level !== "all" || tag !== "all" || q;
    return (
        <>
            <PageTitle
                eyebrow={isVideo ? "کتابخانه تصویری" : "راهنماهای متنی"}
                title={isVideo ? "ویدئوهای آموزشی" : "مقالات و راهنماها"}
                subtitle={isVideo ? "آموزش‌های کوتاه، فصل‌بندی‌شده و قابل جست‌وجو." : "پاسخ‌های دقیق و مرحله‌به‌مرحله برای کار با زوفینو."}
            />
            <div className="w-full p-5 flex justify-content-center">
                <div className="w-8 flex flex-column gap-3">
                    <HelpSearch
                        key={`${kind}-${q}`}
                        className="w-full"
                        initialQuery={q}
                        items={searchItemsList.filter((i) => i.raw && (isVideo ? i.type === "video" : i.type !== "video"))}
                        onOpen={openItem}
                        onSearch={(value) => set({q: value})}
                        placeholder={`جست‌وجو در ${isVideo ? "ویدئوها" : "مقالات"}`}
                    />
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="text-xs text-500">موضوع:</span>
                        <Chip active={cat === "all"} label={T.all} onClick={() => set({cat: "all"})}/>
                        {HELP_CATEGORIES.map((c) => <Chip key={c.id} active={cat === c.id} label={c.title} onClick={() => set({cat: c.id})}/>)}
                    </div>
                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="text-xs text-500">سطح:</span>
                        <Chip active={level === "all"} label={T.all} onClick={() => set({level: "all"})}/>
                        {T.levels.map((l) => <Chip key={l} active={level === l} label={l} onClick={() => set({level: l})}/>)}
                        <span className="text-xs text-500 mr-3">برچسب:</span>
                        {HELP_POPULAR_TAGS.slice(0, 4).map((t) => <Chip key={t} active={tag === t} label={`#${t}`} onClick={() => set({tag: tag === t ? "all" : t})}/>)}
                    </div>
                    <div className="flex align-items-center justify-content-between">
                        <span className="font-bold text-sm">{digits(filtered.length)} {T.results}{cat !== "all" ? ` در ${categoryOf(cat).title}` : ""}</span>
                        {dirty && <Button className="text-xs p-button-text p-1" label={T.clear} onClick={() => go({view: kind})}/>}
                    </div>
                    {filtered.length ? <Cards items={filtered} onOpen={openItem}/> : <Empty onRequest={onRequest}/>}
                </div>
            </div>
            <SupportStrip onRequest={onRequest}/>
        </>
    );
}

/** Results of the main search (articles + videos + CMS posts) with a type filter. */
export function SearchView({params, go, openItem, onRequest, searchItemsList}) {
    const {q = "", type = "all"} = params;
    let results = q ? searchItems(searchItemsList, q, 100) : [];
    if (type !== "all") results = results.filter((i) => (type === "video" ? i.type === "video" : i.type !== "video"));
    results.sort((a, b) => b.popular - a.popular);
    // Feeds "نیازهای بدون پاسخ" in the admin panel.
    useEffect(() => {
        if (q) ping("/help-center/search-log", {query: q, resultCount: results.length});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);
    return (
        <>
            <PageTitle eyebrow="نتایج جست‌وجو" title={q ? `نتیجه برای «${q}»` : "چه چیزی می‌خواهید پیدا کنید؟"} subtitle="نتایج مقاله و ویدئو با برچسب مشخص نمایش داده می‌شوند."/>
            <div className="w-full p-5 flex justify-content-center">
                <div className="w-8 flex flex-column gap-3">
                    <HelpSearch key={q} className="w-full" initialQuery={q} items={searchItemsList} onOpen={openItem} onSearch={(value) => go({view: "search", q: value, type})}/>
                    <div className="flex gap-2">
                        {[["all", T.all], ["article", "مقالات"], ["video", "ویدئوها"]].map(([value, label]) => (
                            <Chip key={value} active={type === value} label={label} onClick={() => go({view: "search", q, type: value})}/>
                        ))}
                    </div>
                    <span className="font-bold text-sm">{digits(results.length)} نتیجه مرتبط</span>
                    {results.length ? results.map((item) => (
                        <div key={item.key} className="border-1 border-300 border-round-xl bg-white p-3 text-xs flex align-items-center gap-3 cursor-pointer" onClick={() => openItem(item)}>
                            <Avatar icon={<Icon icon={`mi mi:${item.icon}`}/>}/>
                            <span className="flex flex-column gap-1">
                                <span className="text-2xs text-400">{[item.label, item.category].filter(Boolean).join(" · ")}</span>
                                <span className="font-bold text-sm">{item.title}</span>
                                {item.summary && <span className="text-400">{item.summary}</span>}
                            </span>
                        </div>
                    )) : <Empty onRequest={onRequest}/>}
                </div>
            </div>
        </>
    );
}

export function CategoryView({id, go, openItem, onRequest}) {
    const category = categoryOf(id);
    const articles = HELP_ARTICLES.filter((i) => i.cat === id);
    const videos = HELP_VIDEOS.filter((i) => i.cat === id);
    return (
        <>
            <PageTitle eyebrow="دسته‌بندی" title={category.title} subtitle={category.desc}/>
            <Section title="مقالات این موضوع" action={T.allArticles} onAction={() => go({view: "articles", cat: id})}><Cards items={articles} onOpen={openItem}/></Section>
            <Section title="ویدئوهای این موضوع" action={T.videoLibrary} onAction={() => go({view: "videos", cat: id})}>
                {videos.length ? <div className="flex flex-wrap">{videos.map((v) => <VideoCard key={v.id} item={v} onOpen={openItem}/>)}</div> : <span className="text-xs text-400">ویدئویی برای این موضوع نیست.</span>}
            </Section>
            <SupportStrip onRequest={onRequest}/>
        </>
    );
}

export function PathView({go, openItem, onRequest}) {
    const outcomes = [
        ["check_circle", "فضای کاری آماده", "نام، تنظیمات اولیه و مالک مشخص است."],
        ["group_add", "تیم دعوت‌شده", "افراد و نقش‌های اولیه تعریف شده‌اند."],
        ["flag", "هدف روشن", "نتیجه مورد انتظار و شاخص‌ها ثبت شده‌اند."],
        ["folder_open", "اولین پروژه", "پروژه، مایلستون و کارهای اولیه آماده‌اند."],
    ];
    return (
        <>
            <PageTitle eyebrow="مسیر پیشنهادی" title="شروع زوفینو در ۲۰ دقیقه" subtitle="فقط چیزهایی که برای شروع واقعی تیم لازم دارید؛ آموزش‌های پیشرفته بعداً."/>
            <Section title="۴ گام">
                <div className="flex flex-column gap-2">
                    {getPathItems().map((item, index) => (
                        <div key={item.id} className="border-1 border-300 border-round-xl bg-white p-3 text-xs flex align-items-center gap-3 cursor-pointer" onClick={() => openItem({kind: item.type === "video" ? "video" : "article", id: item.id})}>
                            <Avatar shape="circle" label={digits(index + 1)}/>
                            <span className="flex flex-column gap-1">
                                <span className="font-bold text-sm">{item.title}</span>
                                <span className="text-400">{item.summary}</span>
                            </span>
                            <span className="mr-auto text-2xs text-400">{typeLabel(item.type)}</span>
                        </div>
                    ))}
                </div>
            </Section>
            <Section tone="surface-100" title="بعد از این مسیر چه چیزی دارید؟">
                <div className="flex flex-wrap">
                    {outcomes.map(([icon, title, description]) => (
                        <div key={title} className="w-3 p-2">
                            <div className="border-1 border-300 border-round-xl bg-white p-3 text-xs flex flex-column gap-2 h-full">
                                <Avatar icon={<Icon icon={`mi mi:${icon}`}/>}/>
                                <span className="font-bold text-sm">{title}</span>
                                <span className="text-400">{description}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
            <SupportStrip onRequest={onRequest}/>
        </>
    );
}

const SECTION_TEXTS = [
    "پیش از شروع، مطمئن شوید وارد فضای کاری درست شده‌اید و دسترسی لازم برای انجام این عملیات را دارید.",
    (c) => `از منوی اصلی وارد بخش «${c}» شوید. دکمه مربوط به ایجاد یا ویرایش را انتخاب کنید و اطلاعات ضروری را کامل کنید.`,
    "عنوان را روشن و نتیجه‌محور بنویسید. مالک، اعضای مرتبط و بازه زمانی را براساس مسئولیت واقعی افراد انتخاب کنید.",
    "در پایان اطلاعات را یک‌بار مرور کنید. پس از ذخیره، مورد جدید در فهرست مربوط نمایش داده می‌شود و برای اعضای مرتبط قابل مشاهده است.",
];
const STEPS = [
    ["بخش موردنظر را باز کنید", "از منوی سمت راست وارد صفحه مرتبط شوید."],
    ["اطلاعات ضروری را وارد کنید", "فیلدهای دارای علامت ضروری را کامل کنید."],
    ["ذخیره و بررسی کنید", "نتیجه را در فهرست اصلی ببینید."],
];

function Feedback() {
    const [answer, setAnswer] = useState(null);
    return (
        <div className="flex align-items-center gap-3 border-1 border-300 border-round-xl p-3 text-xs">
            <b>{answer ? "ممنون از بازخوردتان." : "این محتوا کمکتان کرد؟"}</b>
            {!answer && (
                <>
                    <Button className="p-button-text p-button-secondary p-1" icon="pi pi-thumbs-up" aria-label="مفید بود" onClick={() => setAnswer("yes")}/>
                    <Button className="p-button-text p-button-secondary p-1" icon="pi pi-thumbs-down" aria-label="مفید نبود" onClick={() => setAnswer("no")}/>
                </>
            )}
        </div>
    );
}

function DetailShell({back, backLabel, side, children}) {
    return (
        <div className="w-full p-5 flex justify-content-center">
            <div className="w-10 flex gap-4">
                <aside className="w-3 flex flex-column gap-3 text-xs">
                    <Button className="p-button-text text-xs p-1 w-fit" label={`→ ${backLabel}`} onClick={back}/>
                    {side}
                </aside>
                <main className="flex-1 flex flex-column gap-3 min-w-0">{children}</main>
            </div>
        </div>
    );
}

export function ArticleView({id, go, openItem, onRequest}) {
    const article = HELP_ARTICLES.find((a) => a.id === id) || HELP_ARTICLES[0];
    useEffect(() => {
        if (article?.id) ping("/help-center/view", {id: article.id});
    }, [article?.id]);
    if (!article) return null;
    const category = categoryOf(article.cat);
    const video = HELP_VIDEOS.find((v) => v.related === article.id);
    const related = HELP_ARTICLES.filter((a) => a.cat === article.cat && a.id !== article.id).slice(0, 2);
    return (
        <>
            <DetailShell back={() => go({view: "articles"})} backLabel="همه مقالات" side={(
                <>
                    <div className="border-1 border-300 border-round-xl p-3 flex flex-column gap-2">
                        <span className="font-bold">در این راهنما</span>
                        {article.sections.map((section, index) => (
                            <a key={section} href={`#help-section-${index}`} className="text-600 no-underline" onClick={(e) => { e.preventDefault(); document.getElementById(`help-section-${index}`)?.scrollIntoView({behavior: "smooth"}); }}>{section}</a>
                        ))}
                    </div>
                    <div className="border-1 border-300 border-round-xl p-3 flex flex-column gap-2 cursor-pointer" onClick={() => go({view: "category", cat: article.cat})}>
                        <span className="text-400">همین موضوع</span>
                        <span className="flex align-items-center gap-2 font-bold"><Icon icon={`mi mi:${category.icon}`}/>{category.title}</span>
                        <span className="text-primary">مقاله‌ها و ویدئوها</span>
                    </div>
                </>
            )}>
                <span className="text-xs text-400">خانه / {category.title} / {article.title}</span>
                <Meta item={article}/>
                <span className="text-3xl font-bold">{article.title}</span>
                <span className="text-400">{article.summary}</span>
                <span className="flex flex-wrap gap-2">{tagsOf(article).slice(0, 3).map((t) => <span key={t} className="surface-100 border-round px-2 py-1 text-2xs">#{t}</span>)}</span>
                {video && (
                    <div className="border-1 border-300 border-round-xl p-3 flex align-items-center gap-3 text-xs cursor-pointer surface-50" onClick={() => openItem({kind: "video", id: video.id})}>
                        <Icon icon="mi mi:play_circle" className="text-4xl text-primary"/>
                        <span className="flex flex-column gap-1">
                            <span className="text-400">نسخه ویدئویی همین راهنما · {video.duration}</span>
                            <span className="font-bold">{video.title}</span>
                        </span>
                        <span className="mr-auto text-primary">صفحه ویدئو</span>
                    </div>
                )}
                <span className="text-sm">در پایان این راهنما می‌توانید این کار را بدون کمک پشتیبانی در فضای کاری خود انجام دهید.</span>
                {article.body && (article.bodyFormat === "markdown" ? <PostContent content={article.body}/> : (
                    <span className="text-sm line-height-3" style={{whiteSpace: "pre-wrap"}}>{article.body}</span>
                ))}
                {!article.body && article.sections.map((section, index) => {
                    const text = SECTION_TEXTS[index % SECTION_TEXTS.length];
                    return (
                        <section key={section} id={`help-section-${index}`} className="flex flex-column gap-2">
                            <span className="text-xl font-bold">{section}</span>
                            <span className="text-sm line-height-3">{typeof text === "function" ? text(category.title) : text}</span>
                            {index === 1 && (
                                <div className="flex flex-column gap-2">
                                    {STEPS.map(([title, description], n) => (
                                        <div key={title} className="flex align-items-center gap-2 border-1 border-200 border-round-lg p-2 text-xs">
                                            <Avatar shape="circle" size="normal" label={digits(n + 1)}/>
                                            <span className="flex flex-column"><b>{title}</b><span className="text-400">{description}</span></span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                })}
                {related.length > 0 && (
                    <>
                        <span className="text-xl font-bold">راهنماهای مرتبط</span>
                        <Cards items={related} onOpen={openItem}/>
                    </>
                )}
                <Feedback/>
            </DetailShell>
            <SupportStrip onRequest={onRequest}/>
        </>
    );
}

// Direct files play natively; any other link (Aparat, YouTube, ...) is embedded as an iframe.
function VideoPlayer({url, title}) {
    if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) {
        return <video className="w-full border-round-xl surface-900" controls preload="metadata" src={url} title={title}/>;
    }
    return <iframe className="w-full border-round-xl border-none surface-900" style={{height: "26rem"}} src={url} title={title} allowFullScreen/>;
}

export function VideoView({id, go, openItem, onRequest}) {
    const video = HELP_VIDEOS.find((v) => v.id === id) || HELP_VIDEOS[0];
    useEffect(() => {
        if (video?.id) ping("/help-center/view", {id: video.id});
    }, [video?.id]);
    if (!video) return null;
    const category = categoryOf(video.cat);
    const article = HELP_ARTICLES.find((a) => a.id === video.related);
    const next = HELP_VIDEOS.filter((v) => v.cat === video.cat && v.id !== video.id).slice(0, 2);
    return (
        <>
            <DetailShell back={() => go({view: "videos"})} backLabel="کتابخانه ویدئو" side={(
                <>
                    <div className="border-1 border-300 border-round-xl p-3 flex flex-column gap-2">
                        <span className="font-bold">فصل‌های ویدئو</span>
                        {video.chapters.map(([title, time]) => <span key={title} className="flex justify-content-between text-600"><span>{title}</span><span className="text-400">{time}</span></span>)}
                    </div>
                    <div className="border-1 border-300 border-round-xl p-3 flex flex-column gap-2 cursor-pointer" onClick={() => go({view: "category", cat: video.cat})}>
                        <span className="flex align-items-center gap-2 font-bold"><Icon icon={`mi mi:${category.icon}`}/>{category.title}</span>
                        <span className="text-primary">همه محتواهای موضوع</span>
                    </div>
                </>
            )}>
                <span className="text-xs text-400">خانه / ویدئوها / {video.title}</span>
                <Meta item={video}/>
                <span className="text-3xl font-bold">{video.title}</span>
                <span className="text-400">{video.summary}</span>
                <span className="flex flex-wrap gap-2">{tagsOf(video).slice(0, 3).map((t) => <span key={t} className="surface-100 border-round px-2 py-1 text-2xs">#{t}</span>)}</span>
                {video.videoUrl ? <VideoPlayer url={video.videoUrl} title={video.title}/> : (
                    <div className="surface-900 border-round-xl h-18rem flex flex-column align-items-center justify-content-center gap-2 text-white">
                        <Icon icon="mi mi:play_circle" className="text-6xl"/>
                        <span className="text-xs">۰۰:۰۰ / {video.duration}</span>
                        <span className="text-2xs text-400">پخش‌کنندهٔ نمونه؛ فایل ویدئو هنوز بارگذاری نشده است.</span>
                    </div>
                )}
                {article && (
                    <div className="border-1 border-300 border-round-xl p-3 flex align-items-center gap-3 text-xs cursor-pointer surface-50" onClick={() => openItem({kind: "article", id: article.id})}>
                        <Icon icon="mi mi:article" className="text-3xl text-primary"/>
                        <span className="flex flex-column gap-1"><span className="text-400">راهنمای متنی همین موضوع</span><span className="font-bold">{article.title}</span></span>
                        <span className="mr-auto text-primary">مشاهده راهنمای متنی</span>
                    </div>
                )}
                <span className="text-xl font-bold">فصل‌های ویدئو</span>
                <div className="flex flex-column gap-1">
                    {video.chapters.map(([title, time], index) => (
                        <div key={title} className="flex align-items-center gap-2 border-1 border-200 border-round-lg p-2 text-xs">
                            <Avatar shape="circle" size="normal" label={digits(index + 1)}/><b>{title}</b><span className="mr-auto text-400">{time}</span>
                        </div>
                    ))}
                </div>
                <span className="text-xl font-bold">متن ویدئو و نکات قابل جست‌وجو</span>
                <span className="text-sm line-height-3">
                    در این آموزش ابتدا وارد بخش {category.title} می‌شویم، اطلاعات اصلی را تکمیل می‌کنیم و سپس نتیجه را در فضای کاری بررسی می‌کنیم. این متن نمونه نشان می‌دهد که محتوای گفتاری ویدئو چگونه در جست‌وجوی هلپ‌سنتر قابل پیدا شدن است. کلیدواژه‌ها: {video.keywords}
                </span>
                {next.length > 0 && (<><span className="text-xl font-bold">ویدئوهای بعدی</span><div className="flex flex-wrap">{next.map((v) => <VideoCard key={v.id} item={v} onOpen={openItem} width="w-6"/>)}</div></>)}
                <Feedback/>
            </DetailShell>
            <SupportStrip onRequest={onRequest}/>
        </>
    );
}
